import {
  ApiaUtil,
  IconButton,
  SortableList,
  SortableListItem,
} from '@apia/components';
import { Box, getVariant, Textarea } from '@apia/theme';
import { makeAutoObservable, toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { uniqueId } from '@apia/notifications';
import { ImageIcon } from './ImageIcon';
import { getSrcFromBase64 } from '../ImageBinaryField/getImageSrcString';
import { getImagesFromDisk } from '../ImageBinaryField/getImage';

export type TGenerateFromImage = {
  base64: string;
  description: string;
};

type TStoredImage = TGenerateFromImage & { id: string };

class ImagesStore {
  images: Map<string, TStoredImage> = new Map();
  selectedId = '';

  constructor(
    images: TGenerateFromImage[],
    public multiple: boolean,
  ) {
    this.images = new Map(
      images.map((c) => {
        const id = uniqueId();
        return [id, { ...c, id }];
      }),
    );
    this.selectedId = this.images.values().next().value?.id || '';
    makeAutoObservable(this);
  }
}

const GetImagesDescription = observer(({ store }: { store: ImagesStore }) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps

  return (
    <Box
      className="images_description_generator"
      {...getVariant('layout.design.projectAnalysis.imagesLoader')}
    >
      <Box className="images_sort_wrapper">
        <SortableList
          onSortChange={(ev) => {
            store.images = new Map(
              ev.map((c) => [
                c.props.id,
                c.props.additionalProps as TStoredImage,
              ]),
            );
          }}
        >
          {[...store.images].map(([k, c]) => (
            <SortableListItem additionalProps={c} id={c.id} key={c.id}>
              <ImageIcon
                deletable={store.multiple}
                onClick={() => {
                  store.selectedId = c.id;
                }}
                onRemove={() => {
                  store.images.delete(k);
                }}
                src={getSrcFromBase64(c.base64 ?? '')}
              />
            </SortableListItem>
          ))}
        </SortableList>
      </Box>
      <Box className="image_editor">
        <Box className="image_preview">
          {store.images.get(store.selectedId) && (
            <img
              src={getSrcFromBase64(
                store.images.get(store.selectedId)!.base64 ?? '',
              )}
            />
          )}
        </Box>
        <Box className="image_description">
          <Textarea
            autoFocus
            disabled={!store.images.get(store.selectedId)}
            onChange={(ev) => {
              const image = store.images.get(store.selectedId);
              if (image) image.description = ev.target.value;
            }}
            value={store.images.get(store.selectedId)?.description}
          />
          {store.multiple && (
            <IconButton
              icon="Attach"
              onClick={() => {
                getImagesFromDisk({ multiple: 'true' }).then((res) => {
                  res.forEach((c) => {
                    const id = uniqueId();
                    store.images.set(id, {
                      base64: c.base64,
                      description: '',
                      id,
                    });
                  });
                });
              }}
              size="Md"
              iconSize="Sm"
              variant="icon-only"
            />
          )}
        </Box>
      </Box>
    </Box>
  );
});

export function getImagesWithDescription({
  multiple,
}: {
  multiple?: boolean;
}): Promise<TGenerateFromImage[]> {
  return new Promise((resolve) => {
    getImagesFromDisk({ multiple: multiple ? 'true' : undefined }).then(
      (images) => {
        const store = new ImagesStore(
          images.map((c) => ({
            base64: c.base64,
            description: '',
          })),
          multiple || false,
        );

        ApiaUtil.instance.modals.open({
          children: <GetImagesDescription store={store} />,
          initialFocusGetter(modalInnerRef) {
            return modalInnerRef.querySelector<HTMLElement>('textarea');
          },
          onConfirm: () => {
            resolve([...toJS(store.images).values()]);
          },
          onCancel() {
            resolve([]);
          },
          size: 'xxxl-fixed',
          noHeader: true,
        });
      },
    );
  });
}
